// Shared compact farm-row markup used by both the Farms list (farms-view.ts) and
// the Clicker list (clicker-view.ts) so the two lists look identical: an online
// dot, the farm name, an optional team badge, a right-aligned meta indicator and a
// chevron. Each caller wires its own click handler and supplies its own meta text
// (e.g. "<N> online · <M> devices").
import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { formatClock, clockIconSvg } from './format';
import { farmLocation } from './farm-locations';
import { FarmKind } from './farm-kind';
import { FarmType } from './farm-type';
/**
 * Inner HTML for one `.clicker-farm-row`: dot + name + team badge + meta +
 * optional "last health check" time + chevron.
 *
 * When `lastSeen` is provided (ISO timestamp string, e.g. the farm's
 * `last_seen`), a clock-icon + local time indicator is rendered in the same
 * visual style devices use for their last-status time (see device-card.ts).
 * Pass `null`/omit to hide it (e.g. the Clicker list, which doesn't show it).
 */
export function farmRowInner(opts) {
    return `
        <span class="status-dot ${opts.online ? 'status-dot--green' : 'status-dot--red'}"></span>
        ${farmFlagHtml(opts.country)}
        <span class="clicker-farm-row__name">${esc(opts.name)}</span>
        ${farmKindBadge(opts.farmKind)}
        ${farmTypeBadge(opts.farmType, opts.farmKind)}
        ${versionBadge(opts.version, opts.latestVersion, opts.updateAvailable)}
        ${teamBadge(opts.teamName)}
        <span class="clicker-farm-row__meta">${opts.meta}</span>
        ${lastSeenIndicator(opts.lastSeen)}
        <span class="clicker-farm-row__chevron">›</span>
    `;
}
/**
 * Clock-icon + local time for a farm's last health check (heartbeat), matching
 * the device cards' `.device-updated` indicator. Renders nothing when the farm
 * has never reported (null/missing/unparseable).
 */
function lastSeenIndicator(lastSeen) {
    if (!lastSeen)
        return '';
    const ts = Date.parse(lastSeen);
    if (Number.isNaN(ts))
        return '';
    return `<span class="device-updated farm-row-seen" title="${t('nav.farm_last_health_check', { time: esc(new Date(ts).toLocaleString()) })}">${clockIconSvg}<span class="device-updated__time">${esc(formatClock(ts))}</span></span>`;
}
/**
 * Country flag emoji for a farm's stored location code (e.g. 'AR' → 🇦🇷).
 * Renders nothing when the location is unset ("Not set"), so unlocated farms
 * simply have no flag.
 *
 * Exported so any surface listing farms (the My Devices Config page) shows the same
 * flag from the same definition instead of growing a near-copy.
 */
export function farmFlagHtml(code) {
    const loc = farmLocation(code);
    if (!loc.flag)
        return '';
    return `<span class="farm-row-flag" title="${esc(loc.label)}">${loc.flag}</span>`;
}
/**
 * Image-type tag: "VMOS" for VMOS farms, "REG" for regular host farms (same accent
 * style) — so the type is always shown, not just inferred from VMOS's absence. Aggregator/Duo
 * farms have no image type and already carry their own kind badge (AGG/DUO), so they
 * skip this to keep the row uncluttered.
 */
function farmTypeBadge(farmType, farmKind) {
    if (farmType === FarmType.Vmos) {
        return `<span class="farm-type-badge" title="${t('nav.farm_vmos')}">VMOS</span>`;
    }
    if (farmKind == null || farmKind === FarmKind.Host) {
        return `<span class="farm-type-badge" title="${t('nav.farm_regular')}">REG</span>`;
    }
    return '';
}
/**
 * Topology tag: "AGG" for aggregator farms (remote-phone clients multiplexed as one
 * farm) and "DUO" for DuoPlus cloud-phone farms. Host farms — the default — get no
 * badge, keeping the row uncluttered.
 */
function farmKindBadge(farmKind) {
    if (farmKind === FarmKind.Aggregator) {
        return `<span class="farm-type-badge" title="${t('nav.farm_aggregator')}">AGG</span>`;
    }
    if (farmKind === FarmKind.Duo) {
        return `<span class="farm-type-badge" title="${t('nav.farm_duo')}">DUO</span>`;
    }
    if (farmKind === FarmKind.Geelark) {
        return `<span class="farm-type-badge" title="${t('nav.farm_geelark')}">GEELARK</span>`;
    }
    if (farmKind === FarmKind.Adspower) {
        return `<span class="farm-type-badge" title="${t('nav.farm_adspower')}">ADS</span>`;
    }
    if (farmKind === FarmKind.MercadoApi) {
        return `<span class="farm-type-badge" title="${t('nav.farm_mercado_api')}">MP API</span>`;
    }
    return '';
}
/**
 * Topology + image-type tags together (AGG/DUO/GEELARK, VMOS/REG) — the pair that
 * identifies what a farm actually is. Exported for the same reason as the flag: every
 * farm list renders them from one definition.
 */
export function farmBadgesHtml(farmKind, farmType) {
    return `${farmKindBadge(farmKind)}${farmTypeBadge(farmType, farmKind)}`;
}
/**
 * Baked image-version tag (e.g. "v1.2.6"). When a newer image is published on
 * Docker Hub, the badge switches to an "update available" style and its tooltip
 * names the newer version. Renders nothing when the farm reports no version
 * (old agents), keeping the row uncluttered.
 */
function versionBadge(version, latestVersion, updateAvailable) {
    if (!version)
        return '';
    const label = `v${esc(version)}`;
    if (updateAvailable && latestVersion) {
        return `<span class="farm-version-badge farm-version-badge--update" title="${t('nav.farm_version_update', { latest: esc(latestVersion), version: esc(version) })}">${label} ↑</span>`;
    }
    return `<span class="farm-version-badge" title="${t('nav.farm_version_latest', { version: esc(version) })}">${label}</span>`;
}
/** Small team/group pill (styled like `.role-badge`). Null team → muted dash. */
export function teamBadge(teamName) {
    return teamName
        ? `<span class="team-badge">${esc(teamName)}</span>`
        : '<span class="team-badge team-badge--empty">—</span>';
}
